/**
 * Auth state provider (web/docs/data-layer.md §Auth).
 *
 * Session lifecycle:
 *   - boot → read the persisted refresh token → POST `/auth/refresh` →
 *     restore on success, drop to unauthenticated on 401 (a network error
 *     keeps the stored session, same as the app)
 *   - `signIn(session)` stores a fresh session (email/password or social)
 *   - `GET /v1/me` hydrates the user; email/name come from the session's
 *     id-token claims (`/v1/me` returns `email: null` by design)
 *   - a persistent 401 from the data API clears the session → `/login`
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import * as tokenStore from '@/api/auth/tokenStore';
import { apiGet, UnauthorizedError } from '@/api/http';

import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';

import type { AuthUser, Session } from './types';

interface MeResponse {
  sub?: string | null;
  email?: string | null;
}

/** Confirm the access token works and hydrate display claims. */
async function hydrateUser(session: Session): Promise<AuthUser | null> {
  try {
    const me = await apiGet<MeResponse>('/v1/me');
    return {
      sub: me.sub ?? session.sub,
      email: session.email ?? me.email ?? undefined,
      name: session.name,
    };
  } catch (e) {
    // Dead session — the 401 handler already cleared it and navigated away.
    if (e instanceof UnauthorizedError) return null;
    // Endpoint degraded (404/5xx/offline): keep the user signed in with the
    // claims we already have.
    return { sub: session.sub, email: session.email, name: session.name };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    tokenStore.setOnUnauthorized(() => {
      queryClient.clear();
      setSession(null);
      setUser(null);
      setStatus('unauthenticated');
      navigate('/login', { replace: true });
    });
    return () => tokenStore.setOnUnauthorized(null);
  }, [navigate, queryClient]);

  useEffect(() => {
    let active = true;
    (async () => {
      const stored = tokenStore.loadSession();
      if (!stored) {
        if (active) setStatus('unauthenticated');
        return;
      }
      // Refresh on boot: success → restored; network error → session kept
      // (bearer may be empty until the next refresh); 401 → cleared (null).
      const restored = await tokenStore.refreshSession();
      if (!active) return;
      if (!restored) {
        setStatus('unauthenticated');
        return;
      }
      setSession(restored);
      setStatus('authenticated');
      const me = await hydrateUser(restored);
      if (active) setUser(me);
    })();
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(
    async (next: Session) => {
      tokenStore.saveSession(next);
      queryClient.clear();
      setSession(next);
      setStatus('authenticated');
      setUser(await hydrateUser(next));
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    // Tokens are long-lived JWTs with no server-side revocation — clearing
    // local storage is the whole sign-out (same as the app).
    queryClient.clear();
    tokenStore.clearSession();
    setSession(null);
    setUser(null);
    setStatus('unauthenticated');
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isAuthenticated: status === 'authenticated' && session !== null,
      session,
      user,
      signIn,
      signOut,
    }),
    [status, session, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
